import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Sparkles, Plus, Trash2, Loader2, FileText, ExternalLink, 
  Download, Building2, Users, ChevronDown 
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const AnalysisPage = () => {
  const { api } = useAuth();
  
  // Preset competitors
  const [presetCompetitors, setPresetCompetitors] = useState([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  
  // Leads for dropdown
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // UI state
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('analyze'); // 'analyze', 'history', 'manage'
  
  // Competitor management
  const [newCompetitor, setNewCompetitor] = useState({ name: '', url: '', notes: '' });
  const [savingCompetitor, setSavingCompetitor] = useState(false);

  useEffect(() => {
    fetchPresetCompetitors();
    fetchLeads();
    fetchHistory();
  }, []);

  const fetchPresetCompetitors = async () => {
    try {
      const response = await api.get('/competitors');
      setPresetCompetitors(response.data);
    } catch (error) {
      console.error('Failed to fetch competitors:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads/');
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/analysis/comparison-history');
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const toggleCompetitor = (competitor) => {
    setSelectedCompetitors(prev => {
      const isSelected = prev.some(c => c.id === competitor.id);
      if (isSelected) {
        return prev.filter(c => c.id !== competitor.id);
      } else if (prev.length < 4) {
        return [...prev, competitor];
      } else {
        toast.error('Maximum 4 competitors allowed');
        return prev;
      }
    });
  };

  const runAnalysis = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead/quote to analyze');
      return;
    }

    if (selectedCompetitors.length < 1) {
      toast.error('Please select at least 1 competitor');
      return;
    }

    setAnalyzing(true);
    setReport(null);

    try {
      const response = await api.post('/analysis/compare-lead', {
        lead_id: selectedLead.id,
        competitor_ids: selectedCompetitors.map(c => c.id)
      });
      
      setReport(response.data);
      toast.success('Analysis complete!');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadPDF = async (analysisId) => {
    try {
      const response = await api.get(`/analysis/comparison/${analysisId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FusionBuilderPro_Analysis_${analysisId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const addCompetitor = async () => {
    if (!newCompetitor.name || !newCompetitor.url) {
      toast.error('Name and URL are required');
      return;
    }

    setSavingCompetitor(true);
    try {
      await api.post('/competitors', newCompetitor);
      toast.success('Competitor added!');
      setNewCompetitor({ name: '', url: '', notes: '' });
      fetchPresetCompetitors();
    } catch (error) {
      toast.error('Failed to add competitor');
    } finally {
      setSavingCompetitor(false);
    }
  };

  const deleteCompetitor = async (id) => {
    if (!confirm('Delete this competitor?')) return;
    
    try {
      await api.delete(`/competitors/${id}`);
      toast.success('Competitor deleted');
      fetchPresetCompetitors();
      setSelectedCompetitors(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      toast.error('Failed to delete competitor');
    }
  };

  const loadHistoryReport = (item) => {
    setReport(item);
    setActiveTab('analyze');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-[#C41E3A]" />
              AI Competitor Analysis
            </h1>
            <p className="text-gray-500 dark:text-gray-400">Compare your quotes against competitors with AI-powered insights</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b dark:border-gray-700">
          <button
            onClick={() => setActiveTab('analyze')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'analyze'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            Run Analysis
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            History ({history.length})
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'manage'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Manage Competitors
          </button>
        </div>

        {/* Analysis Tab */}
        {activeTab === 'analyze' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Selection */}
            <div className="space-y-6">
              {/* Lead Selection */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#C41E3A]" />
                  Select Lead/Quote
                </h2>
                <select
                  value={selectedLead?.id || ''}
                  onChange={(e) => {
                    const lead = leads.find(l => l.id === e.target.value);
                    setSelectedLead(lead || null);
                  }}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  data-testid="lead-select"
                >
                  <option value="">-- Select a Lead --</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.customer_name} - ${lead.total_price?.toLocaleString() || 0} 
                      ({new Date(lead.created_at).toLocaleDateString()})
                    </option>
                  ))}
                </select>
                
                {selectedLead && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Selected Quote:</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <strong>Customer:</strong> {selectedLead.customer_name}<br />
                      <strong>Email:</strong> {selectedLead.customer_email}<br />
                      <strong>Total:</strong> ${selectedLead.total_price?.toLocaleString() || 'N/A'}<br />
                      <strong>Style:</strong> {selectedLead.configuration?.style || 'N/A'}<br />
                      <strong>Size:</strong> {selectedLead.configuration?.width || 0}' × {selectedLead.configuration?.length || 0}'
                    </p>
                  </div>
                )}
              </div>

              {/* Competitor Selection */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#C41E3A]" />
                    Select Competitors (1-4)
                  </h2>
                  <span className="text-sm text-gray-500">
                    {selectedCompetitors.length}/4 selected
                  </span>
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {presetCompetitors.map(competitor => (
                    <label 
                      key={competitor.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCompetitors.some(c => c.id === competitor.id)
                          ? 'bg-[#C41E3A]/10 border-2 border-[#C41E3A]'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <Checkbox
                        checked={selectedCompetitors.some(c => c.id === competitor.id)}
                        onCheckedChange={() => toggleCompetitor(competitor)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium block truncate">{competitor.name}</span>
                        <span className="text-xs text-gray-500 truncate block">{competitor.url}</span>
                      </div>
                    </label>
                  ))}
                </div>

                <Button 
                  onClick={runAnalysis} 
                  disabled={analyzing || !selectedLead || selectedCompetitors.length < 1}
                  className="w-full mt-6 bg-[#C41E3A] hover:bg-[#a01830]"
                  data-testid="run-analysis-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing (this may take a minute)...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Run AI Analysis
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Analysis Results</h2>
              
              {report ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{report.customer_name}</p>
                      <p className="text-sm text-gray-500">
                        vs {report.competitors?.length || 0} competitors
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPDF(report.id)}
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  
                  {report.success ? (
                    <div className="prose prose-sm max-w-none max-h-[600px] overflow-y-auto">
                      <ReactMarkdown>{report.analysis}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-700">
                        Analysis failed: {report.error || 'Unknown error'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a lead and competitors, then run the analysis</p>
                  <p className="text-sm mt-2">Results will appear here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Analysis History</h2>
            {history.length > 0 ? (
              <div className="space-y-3">
                {history.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => loadHistoryReport(item)}
                    >
                      <p className="font-medium">{item.customer_name}</p>
                      <p className="text-sm text-gray-500">
                        vs {item.competitors?.map(c => c.name).join(', ')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(item.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadPDF(item.id)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => loadHistoryReport(item)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No analysis history yet</p>
            )}
          </div>
        )}

        {/* Manage Competitors Tab */}
        {activeTab === 'manage' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Add New Competitor */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Add New Competitor</h2>
              <div className="space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={newCompetitor.name}
                    onChange={(e) => setNewCompetitor({...newCompetitor, name: e.target.value})}
                    placeholder="e.g., ABC Metal Buildings"
                  />
                </div>
                <div>
                  <Label>Website URL *</Label>
                  <Input
                    value={newCompetitor.url}
                    onChange={(e) => setNewCompetitor({...newCompetitor, url: e.target.value})}
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={newCompetitor.notes}
                    onChange={(e) => setNewCompetitor({...newCompetitor, notes: e.target.value})}
                    placeholder="Any notes about this competitor..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={addCompetitor}
                  disabled={savingCompetitor}
                  className="w-full bg-[#C41E3A] hover:bg-[#a01830]"
                >
                  {savingCompetitor ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Competitor
                </Button>
              </div>
            </div>

            {/* Existing Competitors */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">
                Preset Competitors ({presetCompetitors.length})
              </h2>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {presetCompetitors.map(competitor => (
                  <div 
                    key={competitor.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{competitor.name}</p>
                      <p className="text-xs text-gray-500 truncate">{competitor.url}</p>
                      {competitor.notes && (
                        <p className="text-xs text-gray-400 mt-1 truncate">{competitor.notes}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCompetitor(competitor.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalysisPage;
